/**
 * Register every LMDR Web Component with `customElements`.
 *
 * Idempotent: safe to call multiple times. Each tag is only defined the first
 * time it is seen.
 */

import { SmootifyProductElement } from "./smootify-product.js";
import { WishlistToggleElement } from "./wishlist-toggle.js";
import { SmootifyAddToCartElement } from "./smootify-add-to-cart.js";
import { SmootifyMarketsSwitcherElement } from "./smootify-markets-switcher.js";

interface Registration {
  tag: string;
  ctor: CustomElementConstructor;
}

/**
 * Custom elements Smootify "inert" — on les register pour que le browser les
 * traite comme HTMLElement proprement (display:inline-block par défaut pour
 * les unknown elements serait OK, mais Webflow Designer les reconnaît mieux
 * quand ils sont déclarés). Aucune logique : la hydration option passe par
 * l'attribut CSS selector `[data-option]`.
 *
 * L'API Custom Elements interdit de réutiliser une même classe pour plusieurs
 * tags → on crée 1 subclass par tag.
 */
class VariantSelectorElement extends HTMLElement {}
class VariantDropdownElement extends HTMLElement {}
class VariantSwatchesElement extends HTMLElement {}
class QuantityInputElement extends HTMLElement {}
class CartItemElement extends HTMLElement {}

const REGISTRATIONS: Registration[] = [
  { tag: "smootify-product", ctor: SmootifyProductElement },
  { tag: "smootify-add-to-cart", ctor: SmootifyAddToCartElement },
  { tag: "smootify-markets-switcher", ctor: SmootifyMarketsSwitcherElement },
  { tag: "wishlist-toggle", ctor: WishlistToggleElement },
  { tag: "variant-selector", ctor: VariantSelectorElement },
  { tag: "variant-dropdown", ctor: VariantDropdownElement },
  { tag: "variant-swatches", ctor: VariantSwatchesElement },
  { tag: "quantity-input", ctor: QuantityInputElement },
  { tag: "cart-item", ctor: CartItemElement },
];

export function registerElements(): void {
  if (typeof customElements === "undefined") return;

  for (const { tag, ctor } of REGISTRATIONS) {
    if (customElements.get(tag)) continue;
    try {
      customElements.define(tag, ctor);
    } catch (err) {
      // Another script on the page may have already defined the tag. Swallow
      // the error to stay idempotent — the warning is only useful in dev.
      // eslint-disable-next-line no-console
      console.warn(`[lmdr] failed to register <${tag}>`, err);
    }
  }
}
